import * as React from 'react';
import { JobExecution, ListExecutionsResponseBody } from '@lct-jobs/core';
import useRestApi from 'hooks/useRestApi';
import { useExecutionPolling } from 'hooks/useExecutionPolling';
import { mergeExecutions } from 'utils/mergeExecutions';

export interface AccumulatedExecutions {
  readonly executions: JobExecution[];
  readonly loading: boolean;
  readonly error?: Error;
  readonly nextPageToken?: string;
  readonly loadMore: (options?: { limit?: number }) => void;
  readonly refresh: () => void;
}

export interface UseAccumulatedExecutionsOptions {
  /** Changing it starts a fresh list: anything accumulated for the old URL is dropped. */
  url: string;
  /**
   * Pages to fetch automatically before handing paging over to the user. A view that filters the
   * list — by type, or by a search term — can otherwise look empty while matches sit further in.
   */
  autoLoadPages?: number;
}

/**
 * A paged executions list that accumulates rather than replaces, and keeps statuses fresh without
 * disturbing paging. Shared by every executions view so the paging and polling rules only exist in
 * one place.
 */
export const useAccumulatedExecutions = ({
  url,
  autoLoadPages = 0,
}: UseAccumulatedExecutionsOptions): AccumulatedExecutions => {
  const { data, loading, error, fetch } = useRestApi<ListExecutionsResponseBody>(url);

  // Polling re-reads page one through its own request so the response can refresh statuses
  // without touching the paging token: a page-one response always carries page two's token, which
  // would otherwise rewind paging every ten seconds once the user had reached the end.
  const { data: polledData, fetch: pollFetch } = useRestApi<ListExecutionsResponseBody>(url, {
    manual: true,
  });

  const [executions, setExecutions] = React.useState<JobExecution[]>([]);
  const [polledIds, setPolledIds] = React.useState<Set<string> | undefined>(undefined);
  const [nextPageToken, setNextPageToken] = React.useState<string | undefined>(undefined);
  const [autoLoadedPages, setAutoLoadedPages] = React.useState(0);

  const reset = React.useCallback(() => {
    setExecutions([]);
    setNextPageToken(undefined);
    setAutoLoadedPages(0);
    setPolledIds(undefined);
  }, []);

  // A new URL is a different list, so nothing accumulated for the old one should survive it.
  const previousUrl = React.useRef(url);
  React.useEffect(() => {
    if (previousUrl.current === url) return;
    previousUrl.current = url;
    reset();
  }, [url, reset]);

  React.useEffect(() => {
    if (!data?.executions) return;
    setExecutions((previous) => mergeExecutions(previous, data.executions));
    setNextPageToken(data.nextPageToken);
  }, [data]);

  React.useEffect(() => {
    if (!polledData?.executions) return;
    setExecutions((previous) => mergeExecutions(previous, polledData.executions));
    setPolledIds(new Set(polledData.executions.map((execution) => execution.executionId)));
  }, [polledData]);

  const loadMore = React.useCallback(
    (options?: { limit?: number }) => {
      if (!nextPageToken || loading) return;
      fetch({
        method: 'GET',
        params: { nextPageToken, ...(options?.limit ? { limit: options.limit } : {}) },
      }).catch(() => {});
    },
    [nextPageToken, loading, fetch],
  );

  React.useEffect(() => {
    if (nextPageToken && !loading && autoLoadedPages < autoLoadPages) {
      setAutoLoadedPages((count) => count + 1);
      loadMore();
    }
  }, [nextPageToken, loading, autoLoadedPages, autoLoadPages, loadMore]);

  const refresh = React.useCallback(() => {
    reset();
    fetch({ method: 'GET' }).catch(() => {});
  }, [reset, fetch]);

  // The promise is handed back, not swallowed: useExecutionPolling waits on it to skip a tick
  // while a poll is still in flight, and abandons one that never settles.
  const pollRefetch = React.useCallback(
    () => pollFetch({ method: 'GET' }).catch(() => {}),
    [pollFetch],
  );

  // Poll off the accumulated list, not the latest response: after auto-load that response is the
  // oldest page, which would stall polling while a run is still active up top. Once a poll has
  // returned, narrow to it — a poll only re-reads page one, so a run that newer ones pushed below
  // page one can never be refreshed and would keep the ten-second poll running forever.
  const polled = React.useMemo(
    () => ({
      executions: polledIds
        ? executions.filter((execution) => polledIds.has(execution.executionId))
        : executions,
    }),
    [executions, polledIds],
  );
  useExecutionPolling(polled, pollRefetch);

  return {
    executions,
    loading,
    error: error ?? undefined,
    nextPageToken,
    loadMore,
    refresh,
  };
};

export default useAccumulatedExecutions;
